import { validPartialDate } from "./partialDate";
import { isValid, parseISO } from "date-fns";
import type { CaseFieldInputKind } from "../components/field_review/CaseFieldInput";
import type { CaseCriteriaValue, CaseFieldOption, CaseFieldValue } from "../model/caseReview";

export type ParsedFieldValue = { ok: true; value: CaseFieldValue } | { ok: false; message: string };

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const TRUE_WORDS = new Set(["true", "yes"]);
const FALSE_WORDS = new Set(["false", "no"]);
/** How many option labels a refusal names; a long codelist (countries) is cut with a count. */
const OPTIONS_LISTED = 40;
/** The text the model sends to clear a field: every value is typed as a string in the tool schema. */
const NULL_WORD = "null";

/**
 * Turns a value the agent sent into the value the control would have produced, or says why not.
 *
 * <p>The same rules the controls apply: a coded field takes an active option (by id or by
 * label), a date is an ISO calendar day the picker would accept, a number is finite, a boolean is a
 * boolean. `null`, or the word "null", clears the field, as every control lets the reviewer do.
 * A criteria field takes the flags to change; see {@link parseCriteria}.
 */
export function parseProposedValue(kind: CaseFieldInputKind, options: CaseFieldOption[] | undefined,
                                   raw: unknown, current?: CaseFieldValue): ParsedFieldValue {
  if (raw === null || isNullWord(raw)) return { ok: true, value: null };
  switch (kind) {
    case "coded": return parseCoded(options ?? [], raw);
    case "boolean": return parseBoolean(raw);
    case "number": {
      const value = typeof raw === "number" ? raw : typeof raw === "string" && raw.trim() !== "" ? Number(raw) : Number.NaN;
      return Number.isFinite(value) ? { ok: true, value } : { ok: false, message: `${String(raw)} is not a number.` };
    }
    case "partial-date":
      return typeof raw === "string" && validPartialDate(raw.trim())
        ? { ok: true, value: raw.trim() } : { ok: false, message: "Enter a valid year, month and year, or full date." };
    case "date":
      return typeof raw === "string" && ISO_DATE.test(raw) && isValid(parseISO(raw))
        ? { ok: true, value: raw } : { ok: false, message: `${String(raw)} is not a date in YYYY-MM-DD form.` };
    case "datetime":
      return typeof raw === "string" && isValid(parseISO(raw))
        ? { ok: true, value: raw } : { ok: false, message: `${String(raw)} is not an ISO date-time.` };
    case "criteria": return parseCriteria(options, raw, current);
    default:
      return typeof raw === "string" ? { ok: true, value: raw }
        : typeof raw === "number" || typeof raw === "boolean" ? { ok: true, value: String(raw) }
        : { ok: false, message: "This field takes text." };
  }
}

/**
 * A criteria proposal: an object of flags, each true or false, keyed by a criterion's id or label
 * in any case ("medically_significant" or "Medically Significant").
 *
 * <p>A name that is not one of the field's criteria is refused with the allowed names, so the agent
 * can retry instead of losing the flag at Save. A criterion that the proposal does not name keeps its
 * current value, so the draft shows exactly what Save stores and an omitted box is not unticked. A
 * field without criteria refuses every proposal: the catalog serves the criteria with the field.
 */
function parseCriteria(options: CaseFieldOption[] | undefined, raw: unknown, current: CaseFieldValue | undefined): ParsedFieldValue {
  const flags = typeof raw === "string" ? parseJsonObject(raw) : raw;
  if (flags === null || typeof flags !== "object" || Array.isArray(flags)
    || !Object.values(flags as Record<string, unknown>).every((flag) => typeof flag === "boolean")) {
    return { ok: false, message: "A criteria field takes an object of flags, each true or false only." };
  }
  if (!options || options.length === 0) return { ok: false, message: "This field has no criteria to choose from." };
  const named: CaseCriteriaValue = {};
  const unknown: string[] = [];
  for (const [name, flag] of Object.entries(flags as Record<string, boolean>)) {
    const wanted = name.trim().toLowerCase();
    const option = options.find((candidate) => String(candidate.id).toLowerCase() === wanted)
      ?? options.find((candidate) => candidate.label.trim().toLowerCase() === wanted);
    if (!option) unknown.push(name);
    else if (String(option.id) in named && named[String(option.id)] !== flag) {
      return { ok: false, message: `${option.label} is given twice, as true and as false.` };
    } else named[String(option.id)] = flag;
  }
  if (unknown.length > 0) {
    const allowed = options.map((option) => `${option.label} (${option.id})`).join(", ");
    const notCriteria = unknown.length === 1 ? "is not a criterion" : "are not criteria";
    return { ok: false, message: `${unknown.join(", ")} ${notCriteria} of this field. Allowed: ${allowed}.` };
  }
  const kept = current !== null && typeof current === "object" ? current : {};
  return { ok: true, value: { ...kept, ...named } };
}

function parseCoded(options: CaseFieldOption[], raw: unknown): ParsedFieldValue {
  // Historical inactive values remain visible on the form, but cannot enter a new proposal.
  const selectable = options.filter((candidate) => candidate.active !== false && candidate.label.trim() !== "");
  const wanted = String(raw).trim().toLowerCase();
  const option = selectable.find((candidate) => String(candidate.id).toLowerCase() === wanted)
    ?? selectable.find((candidate) => candidate.label.trim().toLowerCase() === wanted);
  if (!option) return { ok: false, message: `${String(raw)} is not one of the options for this field. ${optionList(selectable, wanted)}` };
  return { ok: true, value: option.id };
}

/**
 * The labels a coded field accepts, so the agent can retry with a real one instead of giving up:
 * it only sees the field's id, not its codelist, until a value is refused. The closest to the
 * refused text come first, so a long codelist cut at the cap still shows the one it meant.
 */
function optionList(options: CaseFieldOption[], wanted: string): string {
  if (options.length === 0) return "The field has no active options to choose from.";
  const labels = rankedOptions(options, wanted).slice(0, OPTIONS_LISTED).map((option) => option.label);
  const more = options.length > OPTIONS_LISTED ? ` and ${options.length - OPTIONS_LISTED} more` : "";
  return `Allowed: ${labels.join(", ")}${more}.`;
}

/**
 * The options, closest to `wanted` (lower case) first: label or id starting with it, then
 * containing it, then a label whose word initials it abbreviates ("usa" for "United States"),
 * then the rest. Served order is kept within each rank.
 */
export function rankedOptions(options: CaseFieldOption[], wanted: string): CaseFieldOption[] {
  const rank = (option: CaseFieldOption): number => {
    if (wanted === "") return 3;
    const texts = [option.label.trim().toLowerCase(), String(option.id).toLowerCase()];
    if (texts.some((text) => text.startsWith(wanted))) return 0;
    if (texts.some((text) => text.includes(wanted))) return 1;
    const initials = option.label.toLowerCase().split(/[^a-z0-9]+/).filter((word) => word !== "").map((word) => word[0]).join("");
    if (initials.length > 1 && (wanted.startsWith(initials) || initials.startsWith(wanted))) return 2;
    return 3;
  };
  return options.map((option, index) => ({ option, index, rank: rank(option) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ option }) => option);
}

function isNullWord(raw: unknown): boolean {
  return typeof raw === "string" && raw.trim().toLowerCase() === NULL_WORD;
}

function parseBoolean(raw: unknown): ParsedFieldValue {
  if (typeof raw === "boolean") return { ok: true, value: raw };
  const word = String(raw).trim().toLowerCase();
  if (TRUE_WORDS.has(word)) return { ok: true, value: true };
  if (FALSE_WORDS.has(word)) return { ok: true, value: false };
  return { ok: false, message: `${String(raw)} is not a yes/no value.` };
}

/** The agent sends criteria as JSON text (the tool schema types every value as a string). */
function parseJsonObject(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}
